// Client for the product-owner dashboard APIs served by backend-node-v2 (Postgres-backed).
// Base URL = EnvironmentConfig.nodeService (localhost:3000 in local dev). Auth = MSAL JWT
// (same Bearer token the rest of the app uses), obtained via useJwtToken().

import { EnvironmentConfig } from "@me8eon/config";

export type DashboardWindow = "7" | "15" | "30";

// Either a rolling N-day window, or an explicit custom range ("yyyy-MM-dd", from <= to) —
// mirrors the backend's ?window= vs ?from=&to= query params.
export type DashboardRange = { window: DashboardWindow } | { from: string; to: string };

// Shared multi-select filters — mirrors the backend's ?categories= (taxonomy child ids) and
// ?companies= (company names). Applied on every endpoint below except fetchLastRun/triggerSync/
// fetchUniqueUsers.
export type DashboardFilters = { categories?: string[]; companies?: string[] };

export type DashboardKpis = {
  window: number;
  totalQueries: number;
  totalQueriesDelta: number | null; // null when the prior window predates our earliest data
  categories: number;
  likes: number;
  dislikes: number;
  sentimentPositivePct: number;
  incidentsRaised: number;
};
export type DashboardCategory = { parent: string; child: string; category: string; queries: number; share: number };
export type DashboardCategoryQuery = {
  text: string | null;
  sentiment: string | null; // "like" | "dislike" | null
  timestamp: string;
  source: string | null;
  language: string | null;
  company: string | null;
};
export type DashboardCategoryQueries = {
  window: number;
  parent: string;
  child: string;
  category: string;
  total: number;
  limit: number;
  offset: number;
  items: DashboardCategoryQuery[];
};
export type DashboardSentiment = { category: string; likes: number; dislikes: number; positives: string[]; negatives: string[] };

type TokenFn = () => Promise<string | undefined>;

const BASE = `${EnvironmentConfig.nodeService}/service/dashboard`;

/** Builds the ?window= or ?from=&to= query params for a DashboardRange. */
function rangeParams(range: DashboardRange): URLSearchParams {
  return "from" in range ? new URLSearchParams({ from: range.from, to: range.to }) : new URLSearchParams({ window: range.window });
}

// Appends ?categories=&companies= as repeated params (?x=a&x=b), never comma-joined — company
// names may themselves contain commas, so the backend only accepts repeated params for those.
function appendFilters(qs: URLSearchParams, filters?: DashboardFilters): URLSearchParams {
  filters?.categories?.forEach((c) => qs.append("categories", c));
  filters?.companies?.forEach((c) => qs.append("companies", c));
  return qs;
}

async function getJson<T>(path: string, range: DashboardRange, token: TokenFn, filters?: DashboardFilters): Promise<T> {
  const qs = appendFilters(rangeParams(range), filters);
  const res = await fetch(`${BASE}/${path}?${qs.toString()}`, {
    headers: { Authorization: `Bearer ${await token()}` },
  });
  if (!res.ok) throw new Error(`dashboard/${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

/** Map the UI's "30 days" label to the API's "30" window param. */
export function toWindow(range: string): DashboardWindow {
  if (range.startsWith("7")) return "7";
  if (range.startsWith("15")) return "15";
  return "30";
}

export const fetchKpis = (range: DashboardRange, token: TokenFn, filters?: DashboardFilters) => getJson<DashboardKpis>("kpis", range, token, filters);
export const fetchCategories = (range: DashboardRange, token: TokenFn, filters?: DashboardFilters) => getJson<DashboardCategory[]>("categories", range, token, filters);
export const fetchSentiment = (range: DashboardRange, token: TokenFn, filters?: DashboardFilters) => getJson<DashboardSentiment[]>("sentiment", range, token, filters);

// Search-only match counts per category (search bar on Total Queries / Incidents) — a separate,
// lightweight call that never touches fetchCategories'/fetchIncidentCategories' own state.
export type CategoryMatch = { parent: string | null; child: string | null; matches: number };
async function getSearchMatches(path: "categories/search" | "incident-categories/search", range: DashboardRange, search: string, token: TokenFn, filters?: DashboardFilters): Promise<CategoryMatch[]> {
  const qs = appendFilters(rangeParams(range), filters);
  qs.set("search", search);
  const res = await fetch(`${BASE}/${path}?${qs.toString()}`, {
    headers: { Authorization: `Bearer ${await token()}` },
  });
  if (!res.ok) throw new Error(`dashboard/${path} failed: ${res.status}`);
  return res.json() as Promise<CategoryMatch[]>;
}
export const fetchCategoryMatches = (range: DashboardRange, search: string, token: TokenFn, filters?: DashboardFilters) => getSearchMatches("categories/search", range, search, token, filters);
export const fetchIncidentCategoryMatches = (range: DashboardRange, search: string, token: TokenFn, filters?: DashboardFilters) => getSearchMatches("incident-categories/search", range, search, token, filters);

// The full fixed taxonomy ({parent, child, label} only) for the "All Categories" filter dropdown —
// static, no window/filters — so zero-count categories still appear without bundling the taxonomy
// into the frontend build.
export type TaxonomyCategory = { parent: string; child: string; label: string };
export async function fetchTaxonomyCategories(token: TokenFn): Promise<TaxonomyCategory[]> {
  const res = await fetch(`${BASE}/taxonomy-categories`, { headers: { Authorization: `Bearer ${await token()}` } });
  if (!res.ok) throw new Error(`dashboard/taxonomy-categories failed: ${res.status}`);
  return res.json() as Promise<TaxonomyCategory[]>;
}

// E.ON group company/cluster reference list for the company filter dropdown — static, no
// window/filters — fetched instead of bundling ~200 entries into the frontend build.
export type Company = { name: string; id: string; shortName: string; country: string; cluster: string };
export async function fetchCompanies(token: TokenFn): Promise<Company[]> {
  const res = await fetch(`${BASE}/companies`, { headers: { Authorization: `Bearer ${await token()}` } });
  if (!res.ok) throw new Error(`dashboard/companies failed: ${res.status}`);
  return res.json() as Promise<Company[]>;
}

// Contact-request cards (callback + live chat): a total + a per-language breakdown
// (English / Deutsch / Others for the card itself; `languages` carries every individual
// language uncollapsed, for the "Breakdown" drill-down). Window-scoped, and category/company
// filtered like the rest.
export type LanguageCount = { key: string; label: string; count: number };
export type RequestBreakdown = { window: number; total: number; byLanguage: LanguageCount[]; languages: LanguageCount[] };
export const fetchCallbackRequests = (range: DashboardRange, token: TokenFn, filters?: DashboardFilters) => getJson<RequestBreakdown>("callback-requests", range, token, filters);
export const fetchLiveChatRequests = (range: DashboardRange, token: TokenFn, filters?: DashboardFilters) => getJson<RequestBreakdown>("live-chat-requests", range, token, filters);

// MyHelp incidents (Snowflake → Postgres), windowed. Each carries our taxonomy parent/child so the
// UI can category-filter client-side (like categories/sentiment). prevTotal drives the card delta.
export type LiveIncident = {
  id: string;
  issue: string | null;
  parent: string | null;
  child: string | null;
  category: string;
  product: string | null;
  company: string | null;
  severity: string | null; // High | Medium | Low
  status: string | null; // Open | In Progress | Resolved
  openedAt: string;
};
export type DashboardIncidents = { window: number; total: number; prevTotal: number; priorComplete: boolean; items: LiveIncident[] };
export const fetchIncidents = (range: DashboardRange, token: TokenFn, filters?: DashboardFilters) => getJson<DashboardIncidents>("incidents", range, token, filters);

// Incidents grouped by category (breakdown table, same shape/idea as DashboardCategory). parent/child
// are null for unclassified incidents (shown as "Uncategorized" — not clickable, no drill-down).
export type IncidentCategory = { parent: string | null; child: string | null; category: string; incidents: number; share: number };
export const fetchIncidentCategories = (range: DashboardRange, token: TokenFn, filters?: DashboardFilters) => getJson<IncidentCategory[]>("incident-categories", range, token, filters);

// Drill-down (popup): individual incidents within one category.
export type IncidentCategoryDetail = {
  id: string;
  issue: string | null;
  severity: string | null; // High | Medium | Low
  status: string | null; // Open | In Progress | Resolved
  product: string | null;
  company: string | null;
  openedAt: string;
};
export type IncidentCategoryDetails = {
  window: number;
  parent: string;
  child: string;
  category: string;
  total: number;
  limit: number;
  offset: number;
  items: IncidentCategoryDetail[];
};

/** Drill-down: the individual incidents within one category (paginated, newest-first). `companies`
 *  narrows further by the active company filter (no categories param — parent/child already fix it).
 *  `search` narrows to shortDescription matches when the dashboard's search bar has a value. */
export async function fetchIncidentCategoryDetails(
  range: DashboardRange,
  parent: string,
  child: string,
  token: TokenFn,
  limit = 50,
  offset = 0,
  companies?: string[],
  search?: string
): Promise<IncidentCategoryDetails> {
  const qs = rangeParams(range);
  qs.set("parent", parent);
  qs.set("child", child);
  qs.set("limit", String(limit));
  qs.set("offset", String(offset));
  appendFilters(qs, { companies });
  if (search) qs.set("search", search);
  const res = await fetch(`${BASE}/incident-category-details?${qs.toString()}`, {
    headers: { Authorization: `Bearer ${await token()}` },
  });
  if (!res.ok) throw new Error(`dashboard/incident-category-details failed: ${res.status}`);
  return res.json() as Promise<IncidentCategoryDetails>;
}

/** Sort options for the category-queries drill-down table (Sentiment/Date column headers, toggled
 *  on click). Omitted = default (newest-first, same as "date-desc"). */
export type CategoryQuerySort = "date-asc" | "date-desc" | "likes-first" | "dislikes-first" | "no-feedback-first";

/** Drill-down: the individual queries within one category (paginated, newest-first by default).
 *  `companies` narrows further by the active company filter (no categories param — parent/child
 *  already fix it). `search` narrows to queryText matches when the dashboard's search bar has a
 *  value. `sort` reorders by the Sentiment/Date column headers; omitted keeps the newest-first default. */
export async function fetchCategoryQueries(
  range: DashboardRange,
  parent: string,
  child: string,
  token: TokenFn,
  limit = 50,
  offset = 0,
  companies?: string[],
  search?: string,
  sort?: CategoryQuerySort
): Promise<DashboardCategoryQueries> {
  const qs = rangeParams(range);
  qs.set("parent", parent);
  qs.set("child", child);
  qs.set("limit", String(limit));
  qs.set("offset", String(offset));
  appendFilters(qs, { companies });
  if (search) qs.set("search", search);
  if (sort) qs.set("sort", sort);
  const res = await fetch(`${BASE}/category-queries?${qs.toString()}`, {
    headers: { Authorization: `Bearer ${await token()}` },
  });
  if (!res.ok) throw new Error(`dashboard/category-queries failed: ${res.status}`);
  return res.json() as Promise<DashboardCategoryQueries>;
}

export type DashboardLastRun = {
  lastRunAt: string | null; // when the load job last finished
  lastRunKind: string | null; // backfill | daily | window
  latestDataAt: string | null; // newest turn we hold
  earliestDataAt: string | null; // oldest turn we hold (→ span = days of data)
  totalRows: number;
  running: boolean; // a load is currently in flight
  lastSyncError: string | null; // non-null when the most recent sync job(s) failed (e.g. spawn error)
  isAdmin: boolean; // the requesting user may trigger a manual sync
};

export async function fetchLastRun(token: TokenFn): Promise<DashboardLastRun> {
  const res = await fetch(`${BASE}/last-run`, { headers: { Authorization: `Bearer ${await token()}` } });
  if (!res.ok) throw new Error(`dashboard/last-run failed: ${res.status}`);
  return res.json() as Promise<DashboardLastRun>;
}

export type DashboardSyncResult = { started: boolean; alreadyRunning: boolean; running: boolean; message: string };

/** Admin-only: trigger an on-demand incremental load (returns immediately; load runs server-side). */
export async function triggerSync(token: TokenFn): Promise<DashboardSyncResult> {
  const res = await fetch(`${BASE}/sync`, { method: "POST", headers: { Authorization: `Bearer ${await token()}` } });
  if (!res.ok) throw new Error(`dashboard/sync failed: ${res.status}`);
  return res.json() as Promise<DashboardSyncResult>;
}

// "Unique Users" section (Overview) — per day/week/month distinct-user series, each with a
// trend vs the prior equal window and a top-companies breakdown.
export type UniqueUsersGranularity = "day" | "week" | "month";
export type UniqueUsersPoint = { date: string; count: number }; // date = bucket start, "yyyy-MM-dd"
export type UniqueUsersCompanyCount = { company: string; count: number };
export type UniqueUsersTrend = { direction: "up" | "down"; pct: number } | null;
export type UniqueUsersSeries = {
  granularity: UniqueUsersGranularity;
  points: UniqueUsersPoint[];
  total: number;
  trend: UniqueUsersTrend;
  byCompany: UniqueUsersCompanyCount[];
};
export type DashboardUniqueUsers = Record<UniqueUsersGranularity, UniqueUsersSeries>;

/** `env` scopes to one `unique_user_daily.env` value ("all" for every environment); omitted uses the backend default.
 *  `companies` is the only filter this section supports (no window/category filter, unlike the rest of the dashboard). */
export async function fetchUniqueUsers(token: TokenFn, env?: string, companies?: string[]): Promise<DashboardUniqueUsers> {
  const qs = env ? new URLSearchParams({ env }) : new URLSearchParams();
  companies?.forEach((c) => qs.append("companies", c));
  const query = qs.toString();
  const res = await fetch(`${BASE}/unique-users${query ? `?${query}` : ""}`, { headers: { Authorization: `Bearer ${await token()}` } });
  if (!res.ok) throw new Error(`dashboard/unique-users failed: ${res.status}`);
  return res.json() as Promise<DashboardUniqueUsers>;
}
